import React, { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useCategories } from "@/hooks/useCategories";
import { useCommunity } from "@/contexts/CommunityContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslationContext } from "@/contexts/TranslationContext";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { usePostcodeLookup } from "@/hooks/usePostcodeLookup";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { CategorySelect } from './FinalizeRegistrationForm/CategorySelect';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

// Types
export type RegistrationFormData = {
  firstName: string;
  lastName: string;
  userName: string;
  gender: "Male" | "Female";
  city: string;
  postcode: string;
  ageRange: "Under 18" | "18-24" | "25-34" | "35-44" | "45-54" | "55-64" | "65+";
  marketingOptIn: boolean;
  categoryIds: string[];
  termsAgreed: boolean;
};

const schema = yup.object().shape({
  firstName: yup.string().trim().required("First name is required"),
  lastName: yup.string().trim().required("Last name is required"),
  userName: yup.string().trim().required("Username is required"),
  gender: yup
    .mixed<"Male" | "Female">()
    .oneOf(["Male", "Female"])
    .required("Gender is required"),
  city: yup.string().trim().required("City is required"),
  postcode: yup.string().max(32),
  ageRange: yup
    .mixed<"Under 18" | "18-24" | "25-34" | "35-44" | "45-54" | "55-64" | "65+">()
    .oneOf([
      "Under 18",
      "18-24",
      "25-34",
      "35-44",
      "45-54",
      "55-64",
      "65+",
    ])
    .required("Age range is required"),
  marketingOptIn: yup.boolean(),
  categoryIds: yup.array().of(yup.string()).default([]),
  termsAgreed: yup.bool().oneOf([true], "You must agree to the Terms and Conditions."),
});

// Helper function to convert locale to country code
const getCountryCodeFromLocale = (locale: string): string => {
  if (!locale) return 'gb';
  const parts = locale.split('_');
  if (parts.length > 1) {
    return parts[1].toLowerCase();
  }
  const languageToCountry: Record<string, string> = {
    'en': 'gb',
    'fr': 'fr',
    'es': 'es',
    'de': 'de',
    'it': 'it',
    'ar': 'sa',
  };
  return languageToCountry[parts[0].toLowerCase()] || 'gb';
};

export function FinalizeRegistrationForm() {
  const { currentCommunity } = useCommunity();
  const { user, refreshSession } = useAuth();
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { locale } = useTranslationContext();
  const { t } = useTranslation();
  
  const returnTo = searchParams.get('returnTo') || '/';
  const countryCode = getCountryCodeFromLocale(locale);
  const searchPlaceholder = t('search', 'Search');

  const categories = useMemo(() => {
    if (!categoriesData?.categories) return [];
    return categoriesData.categories.map(category => ({
      id: category.id,
      name: category.name || category.translation_key || 'Unnamed category'
    }));
  }, [categoriesData]);
  
  const [loading, setLoading] = useState(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [usernameUnique, setUsernameUnique] = useState<boolean | null>(null);

  const checkUsernameUnique = useCallback(
    async (username: string) => {
      setUsernameError(null);
      if (!username || !currentCommunity?.id) return true;
      
      const { data, error } = await supabase.rpc(
        "check_username_unique_in_community", 
        { 
          _community_id: currentCommunity.id, 
          _username: username 
        }
      );
      
      if (error) {
        console.error("Error checking username uniqueness:", error);
        return false;
      }
      
      const isUnique = data;
      setUsernameError(isUnique ? null : "This username is already taken in this community.");
      setUsernameUnique(isUnique);
      return isUnique;
    },
    [currentCommunity]
  );

  const userFirstName = user?.user_metadata?.first_name || '';
  const userLastName = user?.user_metadata?.last_name || '';

  const defaultValues: RegistrationFormData = useMemo(() => ({
    firstName: userFirstName,
    lastName: userLastName,
    userName: "",
    gender: "Male",
    city: "",
    postcode: "",
    ageRange: "18-24",
    marketingOptIn: false,
    categoryIds: [],
    termsAgreed: false,
  }), [userFirstName, userLastName]);

  const methods = useForm<RegistrationFormData>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues,
  });

  // Postcode lookup hook
  const postcodeLookup = usePostcodeLookup({
    country: currentCommunity.country,
    postcode: methods.watch("postcode") || "",
    onCityFound: (city) => {
      // Auto-populate city field when postcode lookup succeeds
      methods.setValue("city", city, { shouldValidate: true });
    },
  });

  React.useEffect(() => {
    const username = methods.watch("userName");
    let timer: NodeJS.Timeout | null = null;
    if (username && username.length >= 3) {
      setChecking(true);
      setUsernameUnique(null);
      timer = setTimeout(async () => {
        const isUnique = await checkUsernameUnique(username);
        
        if (!isUnique) {
          methods.setError("userName", { 
            type: "manual", 
            message: "This username is already taken in this community." 
          });
        } else {
          methods.clearErrors("userName");
        }
        
        setChecking(false);
      }, 400);
    } else {
      setUsernameError(null);
      setUsernameUnique(null);
      setChecking(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [methods.watch("userName"), checkUsernameUnique, methods]);

  const onSubmit = async (data: RegistrationFormData) => {
    const username = data.userName;
    if (username) {
      setChecking(true);
      const isUnique = await checkUsernameUnique(username);
      setChecking(false);
      
      if (!isUnique) {
        methods.setError("userName", { 
          type: "manual", 
          message: "This username is already taken in this community." 
        });
        return;
      }
    }
    
    setLoading(true);
    if (!currentCommunity || !user) {
      setLoading(false);
      return;
    }

    try {
      console.log("Submitting data:", data);
      console.log("Categories selected:", data.categoryIds);
      console.log("Will redirect to:", returnTo);
      
      const { error: communityError } = await supabase.rpc("add_user_to_community", {
        _user_id: user.id,
        _community_id: currentCommunity.id,
        _firstname: data.firstName,
        _lastname: data.lastName,
        _gender: data.gender,
        _country: currentCommunity.country, // Use country from community
        _town: data.city,
        _postal_code: data.postcode?.toUpperCase() || null,
        _role_name: "user",
        _age_range: data.ageRange,
        _marketing_opt_in: data.marketingOptIn,
        _username: data.userName,
        _category_ids: data.categoryIds || [],
      });

      if (communityError) {
        console.error("Error adding user to community:", communityError);
        
        if (communityError.code === '23505' && communityError.message.includes('username')) {
          methods.setError("userName", { 
            type: "manual", 
            message: "This username is already taken. Please try a different one." 
          });
        } else {
          toast.error("Error completing registration. Please try again.");
        }
        
        setLoading(false);
        return;
      }

      await refreshSession();
      toast.success("Profile completed. Welcome!");
      
      setTimeout(() => {
        navigate(returnTo === '/' ? '/' : returnTo);
      }, 100);
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("An error occurred during registration.");
      setLoading(false);
    }
  };

  const usernameErrorMessage = methods.formState.errors.userName?.message || usernameError;

  return (
    <Form {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className="max-w-xl mx-auto space-y-8"
      >
        {/* Welcome Text */}
        <div className="space-y-2">
          <h2 className="text-2xl font-medium">Complete Your Profile</h2>
          <p className="text-muted-foreground">
            Tell us a bit about yourself to get started with personalized community experiences.
          </p>
        </div>

        {/* Personal Info Grid - Updated layout with firstName, lastName in first row and userName, gender in second row */}
        <div className="grid grid-cols-1 gap-6">
          {/* Name row */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-full sm:w-1/2">
              <FormField
                control={methods.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name <span className="text-destructive">*</span></FormLabel>
                    <FormControl>
                      <Input placeholder="First name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="w-full sm:w-1/2">
              <FormField
                control={methods.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name <span className="text-destructive">*</span></FormLabel>
                    <FormControl>
                      <Input placeholder="Last name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Username and Gender row */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-full sm:w-1/2">
              <FormField
                control={methods.control}
                name="userName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username <span className="text-destructive">*</span></FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input placeholder="Choose a username" {...field} />
                        {checking && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                            <Loader2 className="w-4 h-4 animate-spin" />
                          </div>
                        )}
                        {!checking && usernameUnique === true && field.value.length >= 3 && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500">
                            <CheckCircle className="w-4 h-4" />
                          </div>
                        )}
                        {!checking && usernameUnique === false && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-destructive">
                            <AlertCircle className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                    </FormControl>
                    {usernameErrorMessage && (
                      <p className="text-sm font-medium text-destructive mt-2">
                        {usernameErrorMessage}
                      </p>
                    )}
                    {checking && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Checking availability...
                      </p>
                    )}
                    {!checking && usernameUnique === true && field.value.length >= 3 && (
                      <p className="text-sm text-green-500 mt-2">
                        Username is available!
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground mt-2">
                      Your username will be displayed in chat conversations with business owners instead of your full name for privacy.
                    </p>
                  </FormItem>
                )}
              />
            </div>
            <div className="w-full sm:w-1/2">
              <FormField
                control={methods.control}
                name="gender"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gender <span className="text-destructive">*</span></FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Age Range */}
          <FormField
            control={methods.control}
            name="ageRange"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Age Range <span className="text-destructive">*</span></FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select age range..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Under 18">Under 18</SelectItem>
                    <SelectItem value="18-24">18-24</SelectItem>
                    <SelectItem value="25-34">25-34</SelectItem>
                    <SelectItem value="35-44">35-44</SelectItem>
                    <SelectItem value="45-54">45-54</SelectItem>
                    <SelectItem value="55-64">55-64</SelectItem>
                    <SelectItem value="65+">65+</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Location Info */}
        <div className="grid grid-cols-1 gap-6">
          <FormItem>
            <FormLabel>Country <span className="text-destructive">*</span></FormLabel>
            <div className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
              {currentCommunity.country}
            </div>
          </FormItem>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-full sm:w-[30%]">
              <FormField
                control={methods.control}
                name="postcode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="truncate">Postcode</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          onBlur={(e) => {
                            field.onBlur();
                            // Uppercase postcode on blur
                            if (e.target.value) {
                              methods.setValue("postcode", e.target.value.toUpperCase());
                            }
                          }}
                          className={postcodeLookup.isValid === false ? "border-destructive" : ""}
                        />
                        {postcodeLookup.isLoading && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                          </div>
                        )}
                        {postcodeLookup.isValid === true && !postcodeLookup.isLoading && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          </div>
                        )}
                      </div>
                    </FormControl>
                    {postcodeLookup.error && (
                      <p className="text-sm text-amber-600 flex items-center gap-1 mt-1">
                        <AlertCircle className="w-3 h-3" />
                        {postcodeLookup.error}
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="w-full sm:w-[70%]">
              <FormField
                control={methods.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City <span className="text-destructive">*</span></FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    {postcodeLookup.city && field.value === postcodeLookup.city && (
                      <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                        <CheckCircle className="w-3 h-3" />
                        Auto-filled from postcode. You can change it.
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        {/* Categories */}
        <FormField
          control={methods.control}
          name="categoryIds"
          render={({ field }) => (
            <FormItem>
              <FormLabel>What interests you most?</FormLabel>
              <FormControl>
                <CategorySelect
                  categories={categories}
                  selected={field.value || []} 
                  onChange={field.onChange}
                  isLoading={categoriesLoading}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Terms & Marketing */}
        <div className="space-y-6">
          <FormField
            control={methods.control}
            name="termsAgreed"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-2 sm:space-x-3 space-y-0">
                <FormControl className="flex-shrink-0 mt-1">
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="grid gap-1.5 leading-none">
                  <FormLabel className="text-sm font-normal cursor-pointer">
                    I agree to the Terms and Conditions
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
          <FormField
            control={methods.control}
            name="marketingOptIn"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-2 sm:space-x-3 space-y-0">
                <FormControl className="flex-shrink-0 mt-1">
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="grid gap-1.5 leading-none">
                  <FormLabel className="text-sm font-normal cursor-pointer">
                    Keep me updated about community news and offers
                  </FormLabel>
                </div>
              </FormItem>
            )}
          />
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          size="lg"
          className="w-full"
          disabled={loading || categoriesLoading || checking || usernameUnique === false}
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Complete Registration
        </Button>
      </form>
    </Form>
  );
}

export default FinalizeRegistrationForm;
